import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { NAP, SERVICES, AREAS, FORMSPREE_URL } from "../lib/data";
import { TID } from "../lib/testIds";
import GoogleMap from "../components/GoogleMap";
import { useSEO } from "../lib/useSEO";
import { buildBreadcrumbs } from "../lib/breadcrumbs";

export default function Contact() {
  useSEO({
    title: "Contact Act Fast Appliance Repair — Chicago | (773) 888-4663",
    description:
      "Book same-day appliance repair across Chicagoland. Call (773) 888-4663 or fill out our quick form. Hours: Mon–Fri 9–5. Online booking 24/7.",
    path: "/contact",
    jsonLd: buildBreadcrumbs([
      { name: "Home", path: "/" },
      { name: "Contact", path: "/contact" },
    ]),
  });
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    appliance: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill out name, email, phone and message.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(FORMSPREE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          appliance: form.appliance,
          message: form.message,
          _subject: `New repair request from ${form.name}`,
          source_page: "contact",
        }),
      });
      if (!res.ok) throw new Error(`Formspree ${res.status}`);
      setSubmitted(true);
      toast.success("Request received! We'll call you shortly.");
      setForm({ name: "", email: "", phone: "", appliance: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Could not submit — please call (773) 888-4663 directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-black">
      <section className="relative pt-32 pb-12 lg:pt-44 lg:pb-20 border-b border-[#262626]">
        <div className="absolute inset-0 af-grid-noise opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <p className="af-eyebrow mb-4">Get In Touch</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold font-display leading-[0.95] mb-6">
            BOOK A REPAIR. <br />
            <span className="text-[#DC2626]">CALL THE REPAIRMAN.</span>
          </h1>
          <p className="text-lg text-neutral-300 max-w-2xl leading-relaxed">
            Reach Act Fast Appliance Repair by phone, email, or this form. Same-day service across 34
            Chicagoland communities — Skokie, Elgin, Naperville and beyond.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            {submitted ? (
              <div className="af-card p-10 lg:p-14 text-center">
                <CheckCircle2 size={48} className="text-[#DC2626] mx-auto mb-5" />
                <h2 className="text-3xl font-bold font-display mb-3">Request received.</h2>
                <p className="text-neutral-400 mb-6">
                  An Act Fast dispatcher will call you within one business hour to confirm your appointment.
                </p>
                <button onClick={() => setSubmitted(false)} className="af-btn-outline">
                  Send Another Request
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                action={FORMSPREE_URL}
                method="POST"
                name="act-fast-contact"
                className="af-card p-7 lg:p-10"
                data-testid={TID.contactForm}
              >
                <p className="af-eyebrow mb-3">Service Request</p>
                <h2 className="text-3xl font-bold font-display mb-8">Tell us about the problem</h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <Label htmlFor="name" className="text-xs uppercase tracking-wider text-neutral-400">Name</Label>
                    <Input
                      id="name" name="name" value={form.name} onChange={handleChange("name")}
                      data-testid={TID.contactName}
                      className="mt-2 bg-black border-[#262626] focus-visible:ring-[#DC2626] focus-visible:border-[#DC2626] text-white rounded-none h-12"
                      placeholder="Jane Doe"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-xs uppercase tracking-wider text-neutral-400">Phone</Label>
                    <Input
                      id="phone" name="phone" value={form.phone} onChange={handleChange("phone")}
                      data-testid={TID.contactPhone}
                      className="mt-2 bg-black border-[#262626] focus-visible:ring-[#DC2626] focus-visible:border-[#DC2626] text-white rounded-none h-12"
                      placeholder="(312) 555-0117"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <Label htmlFor="email" className="text-xs uppercase tracking-wider text-neutral-400">Email</Label>
                    <Input
                      id="email" name="email" type="email" value={form.email} onChange={handleChange("email")}
                      data-testid={TID.contactEmail}
                      className="mt-2 bg-black border-[#262626] focus-visible:ring-[#DC2626] focus-visible:border-[#DC2626] text-white rounded-none h-12"
                      placeholder="you@email.com"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-neutral-400">Appliance</Label>
                    <Select value={form.appliance} onValueChange={(v) => setForm((f) => ({ ...f, appliance: v }))}>
                      <SelectTrigger className="mt-2 bg-black border-[#262626] text-white rounded-none h-12" data-testid={TID.contactAppliance}>
                        <SelectValue placeholder="Select appliance" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-[#262626] text-white">
                        {SERVICES.map((s) => (
                          <SelectItem key={s.slug} value={s.short} data-testid={`appliance-opt-${s.slug}`}>
                            {s.short}
                          </SelectItem>
                        ))}
                        <SelectItem value="Other">Other / Not sure</SelectItem>
                      </SelectContent>
                    </Select>
                    {/* Hidden field so the appliance value is included in standard form POST too */}
                    <input type="hidden" name="appliance" value={form.appliance} />
                  </div>
                </div>

                <div className="mb-6">
                  <Label htmlFor="message" className="text-xs uppercase tracking-wider text-neutral-400">Describe the issue</Label>
                  <Textarea
                    id="message" name="message" value={form.message} onChange={handleChange("message")}
                    data-testid={TID.contactMessage}
                    className="mt-2 bg-black border-[#262626] focus-visible:ring-[#DC2626] focus-visible:border-[#DC2626] text-white rounded-none min-h-[140px]"
                    placeholder="e.g. Our LG French-door fridge stopped cooling last night. Freezer still works."
                  />
                </div>

                <input type="hidden" name="_subject" value="New repair request from Act Fast website" />
                <input type="hidden" name="source_page" value="contact" />

                <button type="submit" disabled={submitting} className="af-btn-primary w-full justify-center disabled:opacity-60" data-testid={TID.contactSubmit}>
                  <Send size={18} /> {submitting ? "Sending..." : "Request a Repair"}
                </button>
              </form>
            )}
          </div>

          <aside className="lg:col-span-5 space-y-5">
            <div className="af-card p-7">
              <p className="af-eyebrow mb-3">Call Us Directly</p>
              <a href={NAP.phoneTel} className="text-4xl font-extrabold font-display text-[#DC2626] hover:text-white transition-colors block mb-2">
                {NAP.phoneDisplay}
              </a>
              <p className="text-sm text-neutral-400">Monday–Friday, 9 AM–5 PM</p>
            </div>

            <div className="af-card p-7 space-y-4">
              <InfoRow icon={Mail} label="Email" value={NAP.email} href={NAP.emailLink} />
              <InfoRow icon={MapPin} label="Address" value={`${NAP.addressLine1}, ${NAP.city}, ${NAP.state} ${NAP.zip}`} />
              <div className="pt-4 border-t border-[#262626]">
                <div className="flex items-center gap-2 mb-3">
                  <Clock size={16} className="text-[#DC2626]" />
                  <span className="text-xs uppercase tracking-widest text-neutral-400">Business Hours</span>
                </div>
                {NAP.hours.map((h) => (
                  <div key={h.day} className="flex justify-between text-sm py-1.5 border-b border-[#262626] last:border-0">
                    <span className="text-white">{h.day}</span>
                    <span className="text-neutral-400">{h.time}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="af-card p-7">
              <p className="af-eyebrow mb-3">All Service Areas</p>
              <div className="flex flex-wrap gap-1.5">
                {AREAS.map((a) => (
                  <span key={a} className="af-pill">{a}</span>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <GoogleMap />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, href }) {
  const content = (
    <div className="flex items-start gap-3">
      <Icon size={18} className="text-[#DC2626] mt-0.5 shrink-0" />
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-400 mb-1">{label}</div>
        <div className="text-white text-sm">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block hover:opacity-80 transition-opacity">{content}</a> : content;
}
